import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { userIsNiStaff, supplierKeyForUser } from '../../../roles';
import SupplierCapabilityMap from './SupplierCapabilityMap';
import { ACCENT, Stars, StandingBadge, Triad, capsOf, capabilitiesFor, supplierIdentityMatches } from './supplierUi';
import SupplierCapabilitiesView from './SupplierCapabilitiesView';
import { capabilityMatches, evidenceLabel, approvalLabel } from './SupplierCapabilityFields';
import { SampleScore, useSupplierInvestigationScores } from '../investigations/SupplierInvestigations';

const API_BASE = process.env.REACT_APP_API_URL;

// Grid header cell typography (the five fixed columns).
const gth = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'bottom', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)' };

export default function SuppliersLibrary() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  // Use the shared, backend-mirroring helpers (handle arrays, all metadata
  // sources, service*, and the hk-ringer/hk_ringer separator difference).
  const staffRole = userIsNiStaff({ publicMetadata: user?.publicMetadata });
  const supplierKey = supplierKeyForUser({ publicMetadata: user?.publicMetadata });
  const allowed = staffRole || !!supplierKey;

  const [items, setItems] = useState([]);
  const [canManage, setCanManage] = useState(staffRole);
  const sampleScores = useSupplierInvestigationScores(staffRole && canManage);
  const sampleBySupplier = useMemo(() => Object.fromEntries((sampleScores.data || []).map(item => [item.supplierId, item])), [sampleScores.data]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState({ standing: '', supplierType: '', capability: '', search: '' });
  const [types, setTypes] = useState([]);
  const [standings, setStandings] = useState([]);
  const [capCats, setCapCats] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiNeed, setAiNeed] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const loadSeq = useRef(0);

  const askAI = async () => {
    if (!aiNeed.trim()) return;
    setAiLoading(true); setAiError(''); setAiResult(null);
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/suppliers/recommend`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ need: aiNeed }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || `Failed (${res.status})`);
      setAiResult(data.result);
    } catch (e) { setAiError(e.message); } finally { setAiLoading(false); }
  };

  const runImport = async () => {
    if (!window.confirm('Import the researched supplier list (GPV, Acmepoint, Aurightec, Zenithus, EATHU, HK Ringer, Newstar, Yahama)? Existing suppliers are skipped — your edits are never overwritten.')) return;
    setImporting(true); setImportMsg('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/suppliers/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || `Failed (${res.status})`);
      setImportMsg(`Imported ${data.createdCount} supplier(s)${data.skippedCount ? `, skipped ${data.skippedCount} already present` : ''}.`);
      load();
    } catch (e) { setImportMsg(`Import failed: ${e.message}`); } finally { setImporting(false); }
  };

  // Sequence-guarded: a slower earlier response can never overwrite a newer one.
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true); setLoadError('');
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (filters.standing) params.set('standing', filters.standing);
      if (filters.supplierType) params.set('supplierType', filters.supplierType);

      const res = await window.fetch(`${API_BASE}/quality/suppliers?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (seq !== loadSeq.current) return;
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to load suppliers');
      setItems(data.suppliers || []); setCanManage(!!data.canManage);
    } catch (error) { if (seq === loadSeq.current) setLoadError(error.message); } finally { if (seq === loadSeq.current) setLoading(false); }
  }, [getToken, filters.standing, filters.supplierType]);

  useEffect(() => {
    if (!allowed) { navigate('/quality'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/suppliers/meta/types`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (d.ok) { setTypes(d.types || []); setStandings(d.standings || []); setCapCats(d.capabilityCategories || []); } })
    );
  }, [allowed, navigate, getToken]);

  // Debounced so typing in the search box fires one fetch, not one per keystroke.
  useEffect(() => {
    if (!allowed) return undefined;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, allowed]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const fs = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '6px 10px', fontSize: 13 };
  const tabBtn = (active) => ({ background: active ? ACCENT : 'transparent', color: active ? '#000' : 'var(--ni-text-muted)', border: `1px solid ${active ? ACCENT : 'var(--ni-border-soft)'}`, borderRadius: 6, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' });

  // Grid columns / map vocabulary: the full canonical list plus any custom
  // categories in use — so gaps ("nobody does this") are as visible as coverage.
  const gridCats = useMemo(() => {
    const used = Array.from(new Set(items.flatMap(capsOf)));
    return capCats.length
      ? [...capCats, ...used.filter(c => !capCats.includes(c)).sort()]
      : used.sort((a, b) => a.localeCompare(b));
  }, [items, capCats]);

  // Capability filtering is client-side (the whole list is already loaded), so
  // switching capability never refetches or unmounts the grid/map. The Map view
  // ignores it — the map has its own capability focus.
  const searched = filters.search ? items.filter(s => supplierIdentityMatches(s, filters.search) || (s.capabilities || []).some(cap => capabilityMatches(cap, { query: filters.search }))) : items;
  const capabilityFiltered = filters.capability ? searched.filter(s => capsOf(s).includes(filters.capability)) : searched;
  const shown = view === 'map' ? searched : capabilityFiltered;
  // Keep the active filter selectable even when no loaded supplier carries it.
  const capOptions = filters.capability && !gridCats.includes(filters.capability)
    ? [filters.capability, ...gridCats] : gridCats;
  const mapFiltersActive = !!(filters.standing || filters.supplierType || filters.search);

  const openSupplier = useCallback((id) => navigate(`/quality/suppliers/${id}`), [navigate]);

  if (!allowed) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Supplier Matrix</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canManage && (
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setView('list')} style={tabBtn(view === 'list')}>List</button>
              <button onClick={() => setView('capabilities')} style={tabBtn(view === 'capabilities')}>Capabilities</button>
              <button onClick={() => setView('grid')} style={tabBtn(view === 'grid')}>Grid</button>
              <button onClick={() => setView('map')} style={tabBtn(view === 'map')}>Map</button>
            </div>
          )}
          {canManage && (
            <button onClick={runImport} disabled={importing}
              title="Import the researched supplier list from M365 (create-only — skips suppliers already present)"
              style={{ background: 'transparent', color: ACCENT, border: `1px solid ${ACCENT}66`, borderRadius: 6, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: importing ? 'wait' : 'pointer', opacity: importing ? 0.6 : 1 }}>
              {importing ? 'Importing…' : '↥ Import suppliers'}
            </button>
          )}
          {canManage && (
            <button onClick={() => navigate('/quality/suppliers/new')}
              style={{ background: ACCENT, color: '#000', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              + New Supplier
            </button>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 18px', color: 'var(--ni-text-muted)', fontSize: 13, maxWidth: 760 }}>
        {canManage
          ? 'Find suppliers and compare documented processes, facilities, scope and conditions in Capabilities. Grid and Map show recorded categories; evidence and NI assessment are recorded separately for each process.'
          : 'Your company record. Keep your profile, contacts and certifications up to date in the “Supplier-provided info” section.'}
      </p>

      {importMsg && (
        <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 13,
          background: importMsg.startsWith('Import failed') ? 'rgba(236,33,36,0.1)' : 'rgba(70,187,137,0.1)',
          border: `1px solid ${importMsg.startsWith('Import failed') ? 'rgba(236,33,36,0.3)' : 'rgba(70,187,137,0.3)'}`,
          color: importMsg.startsWith('Import failed') ? '#EC2124' : 'var(--ni-green)' }}>
          {importMsg}
        </div>
      )}

      {canManage && (
        <div style={{ marginBottom: 16, background: 'var(--ni-panel)', border: `1px solid ${aiOpen ? ACCENT + '66' : 'var(--ni-border-soft)'}`, borderRadius: 10, padding: aiOpen ? 16 : '10px 16px' }}>
          <button onClick={() => setAiOpen(o => !o)} style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>
            🔮 Ask: which supplier should I use? {aiOpen ? '▲' : '▾'}
          </button>
          {aiOpen && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <textarea value={aiNeed} onChange={e => setAiNeed(e.target.value)} rows={2}
                  placeholder="e.g. I need something quick and dirty for a PoC — cheap and fast, quality less important"
                  style={{ flex: 1, minWidth: 280, background: 'var(--ni-bg, #0e0e0e)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
                <button onClick={askAI} disabled={aiLoading || !aiNeed.trim()}
                  style={{ background: ACCENT, color: '#000', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: aiLoading || !aiNeed.trim() ? 'not-allowed' : 'pointer', opacity: aiLoading || !aiNeed.trim() ? 0.6 : 1 }}>
                  {aiLoading ? 'Thinking…' : 'Ask'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginTop: 6 }}>Recommends from the suppliers in this matrix only. Always sanity-check the suggestion.</div>
              {aiError && <div style={{ color: '#EC2124', fontSize: 13, marginTop: 10 }}>{aiError}</div>}
              {aiResult && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--ni-border-soft)', paddingTop: 14 }}>
                  {aiResult.topPick
                    ? <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Top pick: <span style={{ color: ACCENT }}>{aiResult.topPick}</span></div>
                    : <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, color: 'var(--ni-text-muted)' }}>No clear match in the matrix.</div>}
                  {aiResult.reasoning && <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 10 }}>{aiResult.reasoning}</div>}
                  {(aiResult.ranked || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {aiResult.ranked.map((r, i) => {
                        const fitColor = r.fit === 'high' ? '#34d399' : r.fit === 'medium' ? '#f59e0b' : '#94a3b8';
                        return (
                          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: fitColor, minWidth: 52 }}>{r.fit || '—'}</span>
                            <span style={{ fontWeight: 700, fontSize: 13, minWidth: 100 }}>{r.name}</span>
                            <span style={{ fontSize: 13, color: 'var(--ni-text-muted)' }}>{r.why}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(aiResult.caveats || []).length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ni-text-muted)' }}>
                      {aiResult.caveats.map((cv, i) => <div key={i}>⚠ {cv}</div>)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {canManage && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filters.standing} onChange={e => setFilter('standing', e.target.value)} style={fs}>
            <option value="">All standings</option>
            {standings.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.supplierType} onChange={e => setFilter('supplierType', e.target.value)} style={fs}>
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {view !== 'map' && (
            <select value={filters.capability} onChange={e => setFilter('capability', e.target.value)} style={fs}>
              <option value="">All capabilities</option>
              {capOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <input aria-label="Search suppliers and capabilities" placeholder="Search supplier, process, part…" value={filters.search} onChange={e => setFilter('search', e.target.value)} style={{ ...fs, minWidth: 180 }} />
          <span style={{ fontSize: 12, color: 'var(--ni-text-muted)' }}>{shown.length} supplier{shown.length === 1 ? '' : 's'}</span>
        </div>
      )}

      {loadError && <div role="alert" style={{ color: '#EC2124', padding: 12, marginBottom: 12 }}>Could not load suppliers: {loadError} <button onClick={load} style={{ color: ACCENT, background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button></div>}
      {loading && <div role="status" style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>Updating suppliers…</div>}
      <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
        {(view === 'capabilities' && canManage) ? <SupplierCapabilitiesView items={items} category={filters.capability} query={filters.search} onOpenSupplier={openSupplier} /> : loading && shown.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ni-text-muted)' }}>Loading…</div>
        ) : shown.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ni-text-muted)' }}>
            {canManage ? <>No suppliers match.{' '}
              <button onClick={() => navigate('/quality/suppliers/new')} style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: 14 }}>Add one.</button></>
              : 'No supplier record is linked to your account yet — your North Invent contact will set it up.'}
          </div>
        ) : (view === 'map' && canManage) ? (
          <SupplierCapabilityMap items={shown} categories={gridCats} onOpenSupplier={openSupplier} filtersActive={mapFiltersActive} />
        ) : (view === 'grid' && canManage) ? (
          <div>
            <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--ni-text-muted)', borderBottom: '1px solid var(--ni-border-soft)' }}>
              Numbers count recorded process / facility entries. Blank means no record. Open Capabilities for scope, evidence and NI assessment.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <th style={{ ...gth, padding: '10px 16px', position: 'sticky', left: 0, background: 'var(--ni-panel)', zIndex: 1 }}>Supplier</th>
                    <th style={gth}>Standing</th>
                    <th style={gth}>Q·P·S</th>
                    <th style={gth}>Sample reports</th>
                    <th style={{ ...gth, padding: '10px 10px', textAlign: 'center' }}>Lead</th>
                    <th style={{ ...gth, padding: '10px 10px', textAlign: 'center' }}>Risk</th>
                    {gridCats.length === 0 && <th style={gth}>No capabilities recorded</th>}
                    {gridCats.map(cat => (
                      <th key={cat} style={{ padding: '10px 5px', height: 140, verticalAlign: 'bottom' }}>
                        <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', margin: '0 auto', fontSize: 11, fontWeight: 600, color: 'var(--ni-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{cat}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map(s => (
                    <tr key={s._id} onClick={() => openSupplier(s._id)}
                      style={{ borderTop: '1px solid var(--ni-border-soft)', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <td style={{ padding: '10px 16px', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--ni-panel)', zIndex: 1 }}>
                        {s.name}<div style={{ fontSize: 11, fontWeight: 400, color: 'var(--ni-text-muted)' }}>{s.supplierType}{s.region ? ` · ${s.region}` : ''}</div>
                      </td>
                      <td style={{ padding: '10px 14px' }}><StandingBadge standing={s.standing} /></td>
                      <td style={{ padding: '10px 14px' }}><Triad qpf={s.qpf} /></td>
                      <td style={{ padding: '10px 14px' }}><SampleScore supplierId={s._id} summary={sampleBySupplier[s._id]} loading={!sampleScores.data} error={sampleScores.error} /></td>
                      <td style={{ padding: '10px 10px', textAlign: 'center', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap' }}>{s.leadTimeDays != null ? `${s.leadTimeDays}d` : '—'}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'center', color: s.riskLevel === 'High' ? '#EC2124' : s.riskLevel === 'Medium' ? ACCENT : 'var(--ni-text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{s.riskLevel || '—'}</td>
                      {gridCats.length === 0 && <td style={{ padding: '10px 16px', color: 'var(--ni-text-muted)' }}>—</td>}
                      {gridCats.map(cat => {
                        const records = capabilitiesFor(s, cat);
                        return <td key={cat} title={records.length ? records.map(cap => [cap.process || cat, cap.site, evidenceLabel(cap), 'NI: ' + approvalLabel(cap), cap.constraints].filter(Boolean).join(' · ')).join('\n') : 'No capability recorded'} style={{ padding: '10px 5px', textAlign: 'center' }}>
                          {records.length ? <span style={{ color: ACCENT, fontWeight: 700 }}>{records.length}</span> : <span style={{ color: 'var(--ni-text-muted)' }}>—</span>}
                        </td>;

                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--ni-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(0,0,0,0.2)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Doc No.</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Location</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Standing</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Manual overall</th>
                  {canManage && staffRole && <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Sample reports</th>}
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Q·P·S</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Capabilities</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(s => (
                  <tr key={s._id} onClick={() => openSupplier(s._id)}
                    style={{ borderTop: '1px solid var(--ni-border-soft)', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '10px 16px', fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'var(--ni-text-muted)' }}>{s.documentNumber}</td>
                    <td style={{ padding: '10px 16px', fontWeight: 700 }}>{s.name}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--ni-text-muted)' }}>{s.supplierType}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap' }}>{[s.country, s.region].filter(Boolean).join(' · ') || s.location || '—'}</td>
                    <td style={{ padding: '10px 16px' }}><StandingBadge standing={s.standing} /></td>
                    <td style={{ padding: '10px 16px' }}><Stars value={s.overallRating} /></td>
                    {canManage && staffRole && <td style={{ padding: '10px 16px' }}><SampleScore supplierId={s._id} summary={sampleBySupplier[s._id]} loading={!sampleScores.data} error={sampleScores.error} /></td>}
                    <td style={{ padding: '10px 16px' }}><Triad qpf={s.qpf} /></td>
                    <td style={{ padding: '10px 16px', color: 'var(--ni-text-muted)', maxWidth: 280 }}>
                      {capsOf(s).slice(0, 3).join(', ') || '—'}
                      {capsOf(s).length > 3 ? ` +${capsOf(s).length - 3}` : ''}
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : '—'}
                      {s.updatedByName ? <div style={{ fontSize: 11 }}>{s.updatedByName}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
